import classNames from "classnames";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import Modal from "@/components/containers/Modal";
import Button from "@/components/forms/Button";
import Field from "@/components/forms/Field";
import Label from "@/components/forms/Label";
import Radio from "@/components/forms/Radio";
import RadioGroup from "@/components/forms/RadioGroup";
import Text from "@/components/text/Text";

import { useCreateMoodUser, useFetchMoods } from "../services";
import { type Mood } from "../types";

type Props = {
  onDismiss: () => void;
  onSubmitted: () => void;
};

export default function MoodPopup({ onDismiss, onSubmitted }: Props) {
  const { t } = useTranslation("mood.user");
  const [step, setStep] = useState<"mood" | "reason">("mood");
  const [selectedMood, setSelectedMood] = useState<Mood<true>>();
  const [selectedReasonId, setSelectedReasonId] = useState<number>();

  const { data: moods } = useFetchMoods();
  const { mutate: create, isPending } = useCreateMoodUser();

  const submit = (mood: Mood<true>, reasonId?: number) => {
    create(
      {
        mood_id: mood.id,
        reasons: reasonId == null ? [] : [reasonId],
      },
      {
        onSuccess: () => onSubmitted(),
      },
    );
  };

  const onContinue = () => {
    if (!selectedMood) return;

    if (selectedMood.reasons.length === 0) {
      submit(selectedMood);
      return;
    }

    setSelectedReasonId(undefined);
    setStep("reason");
  };

  if (!moods || moods.length === 0) {
    return null;
  }

  return (
    <Modal open size="sm" onClose={onDismiss} showCloseButton>
      <div className="flex flex-col gap-4 pe-8 select-none">
        <Text color="gray" weight="bold">
          {step === "reason"
            ? t("What is your mood today?")
            : t("How is your mood today?")}
        </Text>
        {step === "reason" && selectedMood ? (
          <RadioGroup
            value={selectedReasonId}
            onChange={setSelectedReasonId}
            direction="vertical"
            className="gap-2"
          >
            {selectedMood.reasons.map((reason) => {
              const checked = selectedReasonId === reason.id;

              return (
                <Field key={reason.id} inline>
                  <div
                    className={classNames(
                      "relative flex flex-1 cursor-pointer items-center gap-2 rounded-lg border px-2 py-3",
                      "hover:border-primary-200 hover:bg-primary-50",
                      checked && "border-primary-200 bg-primary-50",
                    )}
                    onClick={() => setSelectedReasonId(reason.id)}
                  >
                    <Radio value={reason.id} size="sm" />
                    <Label className="cursor-pointer text-sm font-medium text-gray-700">
                      {reason.reason}
                    </Label>
                  </div>
                </Field>
              );
            })}
          </RadioGroup>
        ) : (
          <RadioGroup
            value={selectedMood}
            onChange={setSelectedMood}
            direction="vertical"
            className="gap-2"
          >
            {moods.map((mood) => {
              const checked = selectedMood?.id === mood.id;

              return (
                <Field key={mood.id} inline>
                  <div
                    className={classNames(
                      "relative flex flex-1 cursor-pointer items-center gap-2 rounded-lg border px-2 py-3",
                      "hover:border-primary-200 hover:bg-primary-50",
                      checked && "border-primary-200 bg-primary-50",
                    )}
                    onClick={() => setSelectedMood(mood)}
                  >
                    <Radio value={mood} size="sm" />
                    <Label className="cursor-pointer text-sm font-medium">
                      {mood.name}
                    </Label>
                    <div className="ms-auto text-sm">{mood.emoji}</div>
                  </div>
                </Field>
              );
            })}
          </RadioGroup>
        )}
        {step === "reason" ? (
          <Button
            className="w-full"
            isLoading={isPending}
            disabled={selectedReasonId == null}
            onClick={() =>
              selectedMood && submit(selectedMood, selectedReasonId)
            }
          >
            {t("Submit", { ns: "core.shared" })}
          </Button>
        ) : (
          <Button
            className="w-full"
            isLoading={isPending}
            disabled={!selectedMood}
            onClick={onContinue}
          >
            {selectedMood && selectedMood.reasons.length === 0
              ? t("Submit", { ns: "core.shared" })
              : t("Continue", { ns: "core.shared" })}
          </Button>
        )}
      </div>
    </Modal>
  );
}
